// ============================================================================
// TourEngine.js — Moteur de visites guidées réutilisable pour Aramis
//
// Architecture :
//   - TourProvider (context) : état global du tour actif
//   - useTour() : hook pour piloter le tour depuis n'importe quel composant
//   - <TourOverlay/> : overlay + spotlight + tooltip (monté au niveau app)
//
// Un "tour" = tableau d'étapes. Chaque étape cible :
//   - target: "#id-selector" → spotlight sur l'élément + tooltip à côté
//   - target: null → modale centrée (étape intro/outro)
//
// Persistance :
//   - localStorage "aramis_tours_seen" pour le flag "déjà fait"
//   - Firestore tourProgress/{userId} pour tracking admin (complétions,
//     étapes vues, durée moyenne)
// ============================================================================

import { Jauge } from "./Jauge";
import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { alpha } from "./alpha";
import { C } from "./couleurs";
import { navTexte } from "./navConstants";
import { IconeTexte } from "./icones";
import { toast } from "./toasts";

const TourContext = createContext(null);

const LS_KEY = "aramis_tours_seen";

function readSeen() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function writeSeen(data) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch { /* ignore */ }
}

// Envoie la progression en Firestore (silencieusement)
async function trackProgress(userId, tourId, event, meta = {}) {
  if (!userId || !tourId) return;
  try {
    const { addDoc, collection } = await import("firebase/firestore");
    const { db } = await import("./firebase");
    await addDoc(collection(db, "tourProgress"), {
      userId, tourId, event, meta, ts: Date.now(),
    });
  } catch { /* ignore */ }
}

// ─── Provider ──────────────────────────────────────────────────────────────
// Visites guidées FERMÉES (demande de Pierre du 15/10/2026) : `start` n'en
// lance aucune et le dit ; la page « Visites guidées » affiche le même texte.
const VISITES_FERMEES = true;
export const MESSAGE_VISITES_FERMEES = "Les visites guidées se refont une beauté et seront bientôt de retour";

export function TourProvider({ children, aramisSession, onNav }) {
  const [activeTour, setActiveTour] = useState(null);   // { id, title, steps, meta }
  const [stepIdx, setStepIdx] = useState(0);
  const [seen, setSeen] = useState(readSeen());
  const startedAtRef = useRef(null);

  const userId = aramisSession?.id;

  const start = useCallback((tour) => {
    // Fermées : aucune visite ne démarre, d'où qu'on la demande (gel des
    // avoirs, page des visites). Les étapes ci-dessous restent pour leur retour.
    toast.info(MESSAGE_VISITES_FERMEES);
    if (VISITES_FERMEES) return;
    if (!tour?.steps?.length) return;
    setActiveTour(tour);
    setStepIdx(0);
    startedAtRef.current = Date.now();
    trackProgress(userId, tour.id, "start", { totalSteps: tour.steps.length });
  }, [userId]);

  const next = useCallback(() => {
    setStepIdx(idx => {
      if (!activeTour) return idx;
      if (idx + 1 >= activeTour.steps.length) {
        // Terminer
        const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : null;
        trackProgress(userId, activeTour.id, "complete", { durationMs, steps: activeTour.steps.length });
        setSeen(prev => {
          const next = { ...prev, [activeTour.id]: { completedAt: Date.now(), durationMs } };
          writeSeen(next);
          return next;
        });
        setActiveTour(null);
        return 0;
      }
      return idx + 1;
    });
  }, [activeTour, userId]);

  const prev = useCallback(() => {
    setStepIdx(idx => Math.max(0, idx - 1));
  }, []);

  const stop = useCallback((completed = false) => {
    if (activeTour) {
      trackProgress(userId, activeTour.id, completed ? "complete" : "skip", {
        stepReached: stepIdx, totalSteps: activeTour.steps.length,
      });
      if (completed) {
        setSeen(prev => {
          const next = { ...prev, [activeTour.id]: { completedAt: Date.now() } };
          writeSeen(next);
          return next;
        });
      }
    }
    setActiveTour(null);
    setStepIdx(0);
  }, [activeTour, stepIdx, userId]);

  const resetTour = useCallback((tourId) => {
    setSeen(prev => {
      const next = { ...prev };
      delete next[tourId];
      writeSeen(next);
      return next;
    });
  }, []);

  // Navigation automatique si l'étape le demande (changement de vue Aramis)
  useEffect(() => {
    if (!activeTour) return;
    const step = activeTour.steps[stepIdx];
    if (step?.onNav && onNav) {
      onNav(step.onNav);
    }
  }, [activeTour, stepIdx, onNav]);

  const value = {
    activeTour, stepIdx, seen,
    start, next, prev, stop, resetTour,
    isSeen: (id) => !!seen[id],
  };

  return (
    <TourContext.Provider value={value}>
      {children}
      {activeTour && <TourOverlay tour={activeTour} stepIdx={stepIdx} onNext={next} onPrev={prev} onStop={stop}/>}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) return { start: () => {}, seen: {}, isSeen: () => false, resetTour: () => {} };
  return ctx;
}

// ─── Overlay + spotlight + tooltip ─────────────────────────────────────────

function TourOverlay({ tour, stepIdx, onNext, onPrev, onStop }) {
  const step = tour.steps[stepIdx];
  const [targetRect, setTargetRect] = useState(null);
  const [viewportSize, setViewportSize] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));

  // Observe la position de la cible (scroll + resize)
  useEffect(() => {
    if (!step) return;
    let raf = null;
    const update = () => {
      if (step.target) {
        const el = document.querySelector(step.target);
        if (el) {
          const r = el.getBoundingClientRect();
          setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
          // Scroll dans la vue si nécessaire
          if (r.top < 80 || r.bottom > window.innerHeight - 80) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        } else {
          setTargetRect(null);  // élément pas présent dans le DOM actuel
        }
      } else {
        setTargetRect(null);
      }
      setViewportSize({ w: window.innerWidth, h: window.innerHeight });
    };
    // Première mise à jour + petit retard pour laisser le DOM se stabiliser
    // après navigation
    update();
    const t = setTimeout(update, 300);
    const loop = () => { update(); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(t);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [step]);

  // Place le tooltip près de la cible (ou centre si pas de cible)
  const tooltipPos = computeTooltipPosition(targetRect, viewportSize, step?.position);

  // Escape pour fermer
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onStop(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onStop]);

  if (!step) return null;

  const isLast = stepIdx === tour.steps.length - 1;
  const isFirst = stepIdx === 0;
  const pct = Math.round(((stepIdx + 1) / tour.steps.length) * 100);
  const spotlightPadding = 8;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10000, pointerEvents: "none" }}>
      {/* Overlay sombre avec trou "spotlight" sur la cible */}
      {targetRect ? (
        <svg width="100%" height="100%" style={{
          position: "absolute", inset: 0, pointerEvents: "auto",
        }}>
          <defs>
            <mask id="tour-spot-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white"/>
              <rect x={targetRect.left - spotlightPadding}
                y={targetRect.top - spotlightPadding}
                width={targetRect.width + spotlightPadding * 2}
                height={targetRect.height + spotlightPadding * 2}
                rx="8" ry="8" fill="black"/>
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%"
            fill="rgba(0, 63, 89,.72)" mask="url(#tour-spot-mask)"/>
          {/* Halo animé autour de la cible */}
          <rect x={targetRect.left - spotlightPadding}
            y={targetRect.top - spotlightPadding}
            width={targetRect.width + spotlightPadding * 2}
            height={targetRect.height + spotlightPadding * 2}
            rx="8" ry="8" fill="none"
            stroke={C.orange} strokeWidth="2.5"
            style={{ filter: `drop-shadow(0 0 10px ${C.orange})` }}>
            <animate attributeName="stroke-opacity" values="1;0.3;1" dur="1.8s" repeatCount="indefinite"/>
          </rect>
        </svg>
      ) : (
        // Pas de cible → fond uni cliquable pour fermer
        <div style={{ position: "absolute", inset: 0, background: "rgba(0, 63, 89,.80)", pointerEvents: "auto" }}/>
      )}

      {/* Tooltip / modale */}
      <div style={{
        position: "absolute", pointerEvents: "auto",
        ...tooltipPos,
        width: Math.min(420, viewportSize.w - 40),
        background: "var(--bg-card, #fff)",
        border: `2px solid ${C.orange}`,
        borderRadius: "var(--radius-lg)", padding: 18,
        boxShadow: "0 12px 40px rgba(0,0,0,.35)",
        color: "var(--text-main, #0a3446)",
      }}>
        {/* Header : icône tour + titre étape + close */}
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: "var(--radius-md)",
            background: `linear-gradient(135deg, ${C.orange}, ${alpha(C.orange, .8)})`,
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "var(--fs-accroche)", fontWeight: 700, flexShrink: 0,
          }}>{tour.icon || <><IconeTexte nom="school"/></>}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "var(--fs-micro)", fontWeight: 700, color: "var(--tx-orange)",
              marginBottom: 2 }}>
              {tour.title}
            </div>
            <div style={{ fontSize: "var(--fs-corps)", fontWeight: 700, color: navTexte(C.navy),
              fontFamily: "Montserrat,sans-serif", lineHeight: 1.3 }}>
              {step.title}
            </div>
          </div>
          <button onClick={() => onStop(false)} title="Quitter la visite guidée"
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: "var(--fs-accroche)", color: "var(--text-sub)", padding: "2px 6px",
            }}>✕</button>
        </div>

        {/* Contenu de l'étape */}
        <div style={{ fontSize: "var(--fs-meta)", lineHeight: 1.6, color: "var(--text-main)", marginBottom: 14 }}>
          {typeof step.content === "string"
            ? <div dangerouslySetInnerHTML={{ __html: step.content }}/>
            : step.content}
        </div>

        {/* Barre de progression */}
        <Jauge valeur={pct} couleur={`linear-gradient(90deg, ${C.teal}, ${C.orange})`} piste="var(--border-color)" hauteur={4} rayon={2} style={{ marginBottom: 10 }} />

        {/* Navigation */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ fontSize: "var(--fs-micro)", color: "var(--text-sub)", fontWeight: 600 }}>
            Étape {stepIdx + 1} / {tour.steps.length}
          </div>
          <span style={{ flex: 1 }}/>
          <button onClick={() => onStop(false)}
            style={{
              fontSize: "var(--fs-micro)", padding: "6px 12px", borderRadius: "var(--radius-sm)",
              background: "transparent", color: "var(--text-sub)",
              border: "1px solid var(--border-color)", cursor: "pointer", fontWeight: 600,
            }}>
            Quitter
          </button>
          {!isFirst && (
            <button onClick={onPrev}
              style={{
                fontSize: "var(--fs-micro)", padding: "7px 14px", borderRadius: "var(--radius-sm)",
                background: "transparent", color: navTexte(C.navy),
                border: `1px solid ${alpha(C.navy, .333)}`, cursor: "pointer", fontWeight: 700,
              }}>
              ← Précédent
            </button>
          )}
          <button onClick={onNext}
            style={{
              fontSize: "var(--fs-micro)", padding: "7px 16px", borderRadius: "var(--radius-sm)",
              background: C.orange, color: "#fff",
              border: "none", cursor: "pointer", fontWeight: 700,
              fontFamily: "Montserrat,sans-serif",
            }}>
            {isLast ? "✓ Terminer" : "Suivant →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Positionnement du tooltip ─────────────────────────────────────────────
function computeTooltipPosition(targetRect, viewport, preferredPosition) {
  const ttW = 420;
  const ttH = 280; // estimation
  const gap = 16;

  // Pas de cible → centre de l'écran
  if (!targetRect) {
    return {
      top: Math.max(20, viewport.h / 2 - ttH / 2),
      left: Math.max(20, viewport.w / 2 - ttW / 2),
    };
  }

  const positions = {
    bottom: {
      top: targetRect.top + targetRect.height + gap,
      left: targetRect.left + targetRect.width / 2 - ttW / 2,
    },
    top: {
      top: targetRect.top - ttH - gap,
      left: targetRect.left + targetRect.width / 2 - ttW / 2,
    },
    right: {
      top: targetRect.top,
      left: targetRect.left + targetRect.width + gap,
    },
    left: {
      top: targetRect.top,
      left: targetRect.left - ttW - gap,
    },
  };

  // Try preferred position first, fallback to bottom then top
  const tryOrder = preferredPosition
    ? [preferredPosition, "bottom", "top", "right", "left"]
    : ["bottom", "top", "right", "left"];

  for (const p of tryOrder) {
    const pos = positions[p];
    if (!pos) continue;
    const fits = pos.top >= 10 && pos.top + ttH <= viewport.h - 10
              && pos.left >= 10 && pos.left + ttW <= viewport.w - 10;
    if (fits) return pos;
  }

  // Aucune position ne tient : place en bas en adaptant left pour rester dans l'écran
  return {
    top: Math.min(viewport.h - ttH - 20, Math.max(20, targetRect.top + targetRect.height + gap)),
    left: Math.max(20, Math.min(viewport.w - ttW - 20, targetRect.left)),
  };
}
